// 帳號 VMS 版面列（MV.3，docs/mycam/mv-spec.md ②）：多路即時同看、多台同步回放共用。
//   開：已存的版面，或直接把「裝置分組」當版面開；只開這頁開得了的（例如在線）、最多 max 台，其餘提示。
//   存 / 改名 / 刪除：寫 /v3/me/preferences 的 vms_layouts（重讀 → 改 → 寫回，其他偏好鍵保留）。
//   設備清單還沒載完不准存（避免存成空牆）；開頁時把帳號下已經沒有的設備從版面拿掉、寫回並提示。

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeviceGroupOrder } from '../../lib/api';
import {
  addLayout,
  deleteLayout,
  LayoutError,
  loadLayouts,
  LAYOUT_NAME_MAX,
  openPlan,
  pruneLayouts,
  renameLayout,
  updateLayouts,
  type VmsLayout,
} from '../../lib/vms/layouts';
import { cameraClass, type DeviceSummary } from '../devices/deviceModel';
import { devicesInGroup, orderedGroups } from '../devices/groups';

interface Props {
  /** 帳號下的設備（useDeviceList；null = 還沒載完） */
  devices: DeviceSummary[] | null;
  /** 這頁開得了的攝影機 id（例如即時同看只開在線的） */
  openable: string[];
  max: number;
  /** 目前選的攝影機（存版面就存這些） */
  selected: string[];
  onOpen: (ids: string[]) => void;
}

type Mode = 'idle' | 'save' | 'rename';

export function LayoutBar({ devices, openable, max, selected, onOpen }: Props) {
  const { t } = useTranslation();
  const [layouts, setLayouts] = useState<VmsLayout[] | null>(null);
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [current, setCurrent] = useState(''); // 選單值：'' / 'lay:<id>' / 'grp:<name>'
  const [mode, setMode] = useState<Mode>('idle');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const delTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cameras = useMemo(() => (devices ?? []).filter((d) => cameraClass(d) === 'camera'), [devices]);
  const accountIds = useMemo(() => new Set(cameras.map((d) => d.device_id)), [cameras]);
  const groups = useMemo(() => orderedGroups(cameras, groupOrder), [cameras, groupOrder]);
  const currentLayout = current.startsWith('lay:') ? layouts?.find((l) => l.id === current.slice(4)) : undefined;
  const loaded = devices !== null;

  // 讀版面；設備清單到了就把帳號下已經沒有的設備拿掉（有動到才寫回）
  useEffect(() => {
    if (!loaded) return;
    let alive = true;
    void getDeviceGroupOrder().then((o) => alive && setGroupOrder(o)).catch(() => {});
    loadLayouts()
      .then(async (ls) => {
        const r = pruneLayouts(ls, accountIds);
        if (r.changed) {
          const removed = ls.reduce((n, l) => n + l.devices.length, 0) - r.layouts.reduce((n, l) => n + l.devices.length, 0);
          const saved = await updateLayouts((cur) => pruneLayouts(cur, accountIds).layouts);
          if (alive) {
            setLayouts(saved);
            setMsg(t('vmsLayout.pruned', { count: removed }));
          }
        } else if (alive) setLayouts(ls);
      })
      .catch(() => alive && setLayouts([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => () => {
    if (delTimer.current) clearTimeout(delTimer.current);
  }, []);

  const open = (value: string) => {
    setCurrent(value);
    setMode('idle');
    setConfirmDel(false);
    if (!value) return;
    let layout: VmsLayout | undefined;
    if (value.startsWith('lay:')) layout = layouts?.find((l) => l.id === value.slice(4));
    else if (value.startsWith('grp:')) {
      const g = value.slice(4);
      layout = { id: '', name: g, devices: devicesInGroup(cameras, g).map((d) => d.device_id), updated_at_us: 0 };
    }
    if (!layout) return;
    const p = openPlan(layout, accountIds, new Set(openable), max);
    onOpen(p.ids);
    const parts: string[] = [];
    if (p.removed) parts.push(t('vmsLayout.removed', { count: p.removed }));
    if (p.skipped) parts.push(t('vmsLayout.skipped', { count: p.skipped, max }));
    setMsg(parts.length ? parts.join(t('ai.sep')) : null);
  };

  const errText = (e: unknown) =>
    e instanceof LayoutError ? t(`vmsLayout.err_${e.code}`, { max: LAYOUT_NAME_MAX }) : t('common.networkError');

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const now = Date.now() * 1000;
      if (mode === 'save') {
        const saved = await updateLayouts((ls) => addLayout(ls, name, selected, now));
        setLayouts(saved);
        const l = saved.find((x) => x.name === name.trim());
        if (l) setCurrent(`lay:${l.id}`);
        setMsg(t('vmsLayout.saved', { name: name.trim() }));
      } else if (mode === 'rename' && currentLayout) {
        setLayouts(await updateLayouts((ls) => renameLayout(ls, currentLayout.id, name, now)));
        setMsg(t('vmsLayout.renamed', { name: name.trim() }));
      }
      setMode('idle');
    } catch (e) {
      setMsg(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!currentLayout) return;
    if (!confirmDel) {
      setConfirmDel(true);
      delTimer.current = setTimeout(() => setConfirmDel(false), 4000);
      return;
    }
    setBusy(true);
    try {
      setLayouts(await updateLayouts((ls) => deleteLayout(ls, currentLayout.id)));
      setMsg(t('vmsLayout.deleted', { name: currentLayout.name }));
      setCurrent('');
    } catch (e) {
      setMsg(errText(e));
    } finally {
      setBusy(false);
      setConfirmDel(false);
    }
  };

  return (
    <div className="vms-lb" data-testid="layout-bar">
      <select
        className="vms-lb-select"
        value={current}
        onChange={(e) => open(e.target.value)}
        aria-label={t('vmsLayout.pick')}
        disabled={layouts === null}
        data-testid="layout-select"
      >
        <option value="">{t('vmsLayout.pick')}</option>
        {layouts && layouts.length > 0 && (
          <optgroup label={t('vmsLayout.saved_group')}>
            {layouts.map((l) => (
              <option key={l.id} value={`lay:${l.id}`}>
                {l.name}（{l.devices.length}）
              </option>
            ))}
          </optgroup>
        )}
        {groups.length > 0 && (
          <optgroup label={t('vmsLayout.groups')}>
            {groups.map((g) => (
              <option key={g} value={`grp:${g}`}>
                {g}（{devicesInGroup(cameras, g).length}）
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {mode === 'idle' ? (
        <>
          <button
            className="btn-ghost"
            disabled={!loaded || layouts === null || selected.length === 0 || busy}
            title={!loaded ? t('vmsLayout.waitDevices') : selected.length === 0 ? t('vmsLayout.pickFirst') : undefined}
            onClick={() => {
              setName(currentLayout?.name ?? '');
              setMode('save');
            }}
            data-testid="layout-save"
          >
            {t('vmsLayout.save')}
          </button>
          {currentLayout && (
            <>
              <button className="btn-ghost" disabled={busy} onClick={() => { setName(currentLayout.name); setMode('rename'); }} data-testid="layout-rename">
                {t('vmsLayout.rename')}
              </button>
              <button className={confirmDel ? 'btn-danger' : 'btn-ghost'} disabled={busy} onClick={remove} data-testid="layout-delete">
                {confirmDel ? t('vmsLayout.confirmDelete') : t('vmsLayout.delete')}
              </button>
            </>
          )}
        </>
      ) : (
        <form
          className="vms-lb-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <input
            autoFocus
            value={name}
            maxLength={LAYOUT_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('vmsLayout.namePlaceholder')}
            aria-label={t('vmsLayout.name')}
            data-testid="layout-name"
          />
          <button type="submit" className="btn-primary" disabled={busy || !name.trim()} data-testid="layout-submit">
            {mode === 'save' ? t('vmsLayout.save') : t('vmsLayout.rename')}
          </button>
          <button type="button" className="btn-ghost" onClick={() => setMode('idle')}>
            {t('common.cancel')}
          </button>
        </form>
      )}

      {msg && (
        <span className="text-secondary vms-lb-msg" role="status" data-testid="layout-msg">
          {msg}
        </span>
      )}
    </div>
  );
}
